/**
 * §A3's count-up: *"count-up over 1200 ms with cubic ease-out, starting immediately (no
 * IntersectionObserver), with a `setTimeout(settle, 1500)` safety net. Under
 * `prefers-reduced-motion: reduce` the box settles instantly at its final value."*
 *
 * **Do not remove the safety net.** `04-officials-profiles.md` §B10 records why: a frame
 * callback that never fires — a backgrounded tab, a throttled timer, a browser that pauses
 * animation on a hidden element — would otherwise leave a progress figure frozen part way to
 * its value, and a stat box showing 2.4% when the panel is at 6.3% is worse than one that
 * never animated at all. The timer settles it regardless of what the frames did.
 *
 * The hook returns the figure only. §B9's rule that a loading surface must *"never render a
 * 0.0% stat box"* is honoured by the caller, which draws a skeleton instead of a box until
 * the payload has arrived — this hook is never mounted with a placeholder value.
 */

import { useEffect, useState } from "react";

const DURATION_MS = 1200;
const SAFETY_NET_MS = 1500;

/**
 * True when the animation must not run.
 *
 * An environment with no `matchMedia` at all — a test renderer, a server render — counts as
 * reduced: settling instantly shows the true figure, and an environment that cannot be asked
 * about motion preferences is not one to animate in on a guess.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Cubic ease-out, as §A3 names it. */
function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

export function useCountUp(target: number): number {
  const [value, setValue] = useState<number>(() => (prefersReducedMotion() ? target : 0));

  useEffect(() => {
    if (prefersReducedMotion() || typeof requestAnimationFrame !== "function") {
      setValue(target);
      return;
    }

    let frame = 0;
    let settled = false;
    const started = performance.now();

    const settle = (): void => {
      if (settled) return;
      settled = true;
      setValue(target);
    };

    const step = (now: number): void => {
      const t = Math.min(1, (now - started) / DURATION_MS);
      if (t >= 1) {
        settle();
        return;
      }
      // One decimal all the way up, so the figure never renders more precision than §A3's
      // `NN.N%` allows and then drops it at the end.
      setValue(Math.round(target * easeOutCubic(t) * 10) / 10);
      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    const net = setTimeout(settle, SAFETY_NET_MS);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(net);
    };
  }, [target]);

  return value;
}
